"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Calendar as CalendarIcon, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
function BriefingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [calendarOpen, setCalendarOpen] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="border-t border-border pt-12">
        <p className="text-2xl md:text-3xl font-default font-medium leading-tight tracking-tight text-foreground">
          {successMessage}
        </p>

        <Button onClick={resetForm} variant="link" className="mt-8 h-auto p-0 text-sm font-medium uppercase tracking-wider text-foreground underline underline-offset-4">
          Submit another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Full name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Smith" {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 h-auto py-1" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Agency or Organization
            </FormLabel>
            <FormControl>
              <Input placeholder="City of Springfield" {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 h-auto py-1" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Email address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jsmith@agency.gov" {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 h-auto py-1" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Phone number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 h-auto py-1" />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Optional — for direct follow-up.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" rules={{
      required: "Please select a project type"
    }} render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Project category
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="border-0 bg-transparent px-0 text-base text-foreground focus-visible:ring-0 focus-visible:border-0 h-auto py-1 data-[placeholder]:text-muted-foreground/60 w-full justify-between">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Infrastructure Planning">
                  Infrastructure Planning
                </SelectItem>
                <SelectItem value="Grant Coordination">
                  Grant Coordination
                </SelectItem>
                <SelectItem value="Compliance Support">
                  Compliance Support
                </SelectItem>
                <SelectItem value="Project Management">
                  Project Management
                </SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="location" render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Project location
            </FormLabel>
            <FormControl>
              <Input placeholder="County or Region" {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 h-auto py-1" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="square_footage" render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Project scale
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g., 5-mile road expansion" {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 h-auto py-1" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" rules={{
      min: {
        value: 0,
        message: "Budget must be positive"
      }
    }} render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Estimated budget (USD)
            </FormLabel>
            <FormControl>
              <Input type="number" placeholder="e.g., 1000000" {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 h-auto py-1" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="timeline" render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4 relative">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Anticipated start date
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Button type="button" variant="ghost" onClick={() => setCalendarOpen(!calendarOpen)} className="w-full h-auto px-0 py-1 justify-between text-base text-left text-foreground hover:bg-transparent">
                  <span className={field.value ? "" : "text-muted-foreground/60"}>
                    {field.value ? new Date(field.value).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
              }) : "Select a date"}
                  </span>
                  <SafeHydrate><CalendarIcon className="size-4 text-muted-foreground" /></SafeHydrate>
                </Button>

                {calendarOpen && <div className="absolute z-50 mt-2 bg-popover border border-border shadow-lg">
                    <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                field.onChange(date ? date.toISOString() : "");
                setCalendarOpen(false);
              }} /></SafeHydrate>
                  </div>}
              </div>
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Target project initiation date.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="description" rules={{
      required: "Project description is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Project summary
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Provide an overview of your infrastructure needs, current challenges, and project goals." {...field} className="border-0 bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-0 resize-none min-h-0 py-1" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="attachments" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="gap-4 border-b border-border pb-4">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
              Upload supporting files
            </FormLabel>
            <FormControl>
              <div className="flex items-center gap-3 py-1">
                <Input type="file" onChange={e => onChange(e.target.files?.[0]?.name || "")} {...field} className="border-0 bg-transparent px-0 text-base text-foreground file:text-foreground file:font-medium focus-visible:ring-0 focus-visible:border-0 h-auto py-0" />
              </div>
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Optional — attach relevant planning documents or RFPs.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-[var(--chart-1)] p-4">
          <p className="text-sm text-[var(--chart-1)]">{submitError}</p>
        </div>}

      <div className="pt-8 flex md:justify-end">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto h-auto px-8 py-5 bg-[var(--chart-1)] text-[var(--primary-foreground)] hover:bg-[var(--chart-1)]/90 text-sm font-medium uppercase tracking-[0.04em]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting...
            </> : <>Submit inquiry →</>}
        </Button>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="15199" id="inquiry-portal" className="bg-background font-default overflow-hidden ![padding:70px_0px_70px_0px]">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div ref={leftRef} className={`w-full max-w-full lg:col-span-5 lg:sticky lg:top-32 lg:self-start transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <h1 className="font-default font-medium text-foreground leading-[1.0] tracking-tight text-[clamp(2.5rem,10vw,5rem)]">
              <span className="block w-full max-w-full break-words !text-[rgba(24,51,78,1)]">
                Contact Us.
              </span>
            </h1>

            <div className="mt-8 space-y-8">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-2">
                  Direct email
                </p>

                <Link to="mailto:admin@infrapm.com" className="text-base text-foreground underline underline-offset-4 decoration-foreground/40 hover:decoration-[var(--chart-1)] hover:text-[var(--chart-1)] transition-colors break-words">
                  admin@infrapm.com
                </Link>
              </div>

              <div>
                <p className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-2">
                  Office line
                </p>

                <div className="text-base text-foreground">
                  El Paso&nbsp;
                  <Link to="tel:+19153351240" className="hover:text-[var(--chart-1)] transition-colors">
                    (915) 335-1240
                  </Link>
                  <br />
                  Austin&nbsp;&nbsp;
                  <Link to="tel:+15125680630" className="hover:text-[var(--chart-1)] transition-colors">
                    (512) 568-0630
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-10 space-y-6 text-base leading-relaxed text-muted-foreground w-full max-w-md">
              <p>
                Contact InfraPM to discuss an infrastructure project or funding
                need. We will follow up shortly with next steps.
              </p>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-7" />
        </div>
      </div>
    </section>;
}